// MODULES
import React from "react";

import FileDrop from "@components/FileDrop/FileDrop";

// HELPERS
import {createClassName, localeNumber} from "@helpers/utils";
import fulfillmentCheck from "./AttachFiles.fulfillment";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import * as CobHelpers from "../../../Cob.helpers";
import i18n from "@helpers/i18n";


export default class AttachFiles extends React.PureComponent {

	static fields = _getFields();
	static companyExclusive = false;
	static privateExclusive = false;
	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	componentDidMount() {
		const {app, partner} = this.props;
		const fulfilledViewsById = {...app.fulfilledViewsById};

		/**
		 * This view should start out per default as fulfilled. Only if some criteria are met should it be required
		 * to attach a file.
		 */
		for (let i = 0; i < partner.sections.length; i++) {
			if (partner.sections[i].name === "AttachFiles") {
				fulfilledViewsById["AttachFiles"] = true;
			}
		}

		this.props.actions.setAppState({fulfilledViewsById});
	}

	render() {
		const {state, props} = this;
		const {app} = props;

		const totalDocsSize = state.docs.reduceRight((a, b) => a + Math.round(b.size / 1024), 0);
		const maxUploadFileSize = 8192; // 8 MB

		const classes = createClassName("Cob-AttachFiles", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
			"size-exceeded": totalDocsSize > maxUploadFileSize
		});

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<h2>{i18n("attach_files", "header")}</h2>
				<p>Här lägger ni de dokument som ska bifogas mötesdokumentationen.</p>

				{app.documentsRequired.length > 0 && (
					<React.Fragment>
						<strong style={{color: "red"}}>Följande dokument måste bifogas:</strong>
						<ul className={"documents-required"}>
							{app.documentsRequired.map(desc => (
								<li key={desc}>- {desc}</li>
							))}
						</ul>
					</React.Fragment>
				)}

				<div className="Cob-AttachFiles-Fields">
					<DocsList
						items={state.docs}
						totalSize={totalDocsSize}
						maxSize={maxUploadFileSize}
						onDocRemove={this._onDocRemove}
					/>

					<FileDrop
						dropText={i18n("attach_files", "drop_documents_hint")}
						busyText="Laddar in dokument..."
						accept="application/pdf"
						onDrop={this._onDocDrop}
					/>
				</div>
				<p style={{fontStyle: "italic", fontSize: "11px", margin: "-22px 10px 0 10px"}}>Filnamn måste vara unika.</p>
			</div>
		);
	}

	_onDocDrop = (documents) => {
		const {props, state} = this;
		const {session} = props;

		const docs = [...state.docs];
		const docNames = [...state.docNames];

		for (let i = 0, n = documents.length; i < n; i++) {
			const doc = documents[i];

			// Prevent duplicates
			if (docNames.indexOf(doc.name) !== -1) {
				continue;
			}

			docs.push({
				name: doc.name,
				size: doc.size
			});

			docNames.push(doc.name);

			session.addDocument({
				name: doc.name,
				size: doc.size,
				data: doc.data
			});
		}

		this.setState({docs, docNames}, () => {
			const {app} = this.props;
			const isFulfilled = fulfillmentCheck(this.props, this.state);
			const fulfilledViewsById = {...app.fulfilledViewsById};
			if (isFulfilled) {
				fulfilledViewsById["AttachFiles"] = true;
			}
			else {
				delete fulfilledViewsById["AttachFiles"];
			}

			const documentsAttached = docs.length;
			if (props.app.documentsRequired.length > 0 && documentsAttached === 0) {
				delete fulfilledViewsById["AttachFiles"];
			}

			this.props.actions.setAppState({fulfilledViewsById, attachedDocuments: docs.length});
		});
	};

	_onDocRemove = (index) => {
		const {props, state} = this;
		const {session} = props;
		const docs = [...state.docs].filter((d, i) => i !== index);
		const docNames = [...state.docNames].filter((d, i) => i !== index);

		session.removeDocumentAtIndex(index);

		this.setState({docs, docNames}, () => {
			const {app} = this.props;
			const isFulfilled = fulfillmentCheck(this.props, this.state);
			const fulfilledViewsById = {...app.fulfilledViewsById};
			if (isFulfilled) {
				fulfilledViewsById["AttachFiles"] = true;
			}
			else {
				delete fulfilledViewsById["AttachFiles"];
			}

			const documentsAttached = docs.length;
			if (props.app.documentsRequired.length > 0 && documentsAttached === 0) {
				delete fulfilledViewsById["AttachFiles"];
			}

			this.props.actions.setAppState({fulfilledViewsById, attachedDocuments: docs.length});
		});
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_fieldValidator = (name, value) => {
		return value.length > 0;
	};

}

// PRIVATE FUNCTIONS
function _getFields() {
	return [];
}

function _getInitialState() {
	if (this.props.prevState) {
		return {...this.props.prevState};
	}

	return {
		isFulfilled: true,
		docs: [],
		docNames: [],
		fields: {},
		validFields: {}
	};
}


// PRIVATE COMPONENTS
class DocsList extends React.PureComponent {

	render() {
		const {props} = this;
		const {items, totalSize, maxSize, onDocRemove} = props;
		const classes = createClassName("Cob-AttachFiles-DocsList", {});

		return (
			<div className={classes}>
				<div className="Cob-AttachFiles-DocsList-info">
					<div className="total-docs">
						<strong>{i18n("attach_files", "number_of_docs")}:</strong>
						<span>{items.length}</span>
					</div>
					<div className="max-size">
						<strong>{i18n("attach_files", "max_total_size")}:</strong>
						<span>{`${localeNumber(totalSize)} KB / ${localeNumber(maxSize)} KB`}</span>
					</div>
				</div>

				<div className="Cob-AttachFiles-DocsList-items">
					{items.length === 0 ? (
						<div
							className="Cob-AttachFiles-DocsList-empty">{i18n("attach_files", "no_documents_hint")}</div>
					) : items.map((item, index) => (
						<DocsListItem key={index} item={item} index={index} onRemove={onDocRemove}/>
					))}
				</div>
			</div>
		);
	}
}

class DocsListItem extends React.PureComponent {

	render() {
		const {item, onRemove} = this.props;
		const size = Math.round(item.size * 100) / 100000;

		return (
			<div className="Cob-AttachFiles-DocsList-Item">
				<div className="left">
					<div className="name">{item.name}</div>
					<div className="size">{localeNumber(size)} KB</div>
				</div>
				<div className="remove-button material-icons"
					 onClick={onRemove ? this._onRemove : undefined}>remove_circle
				</div>
			</div>
		);
	}

	// Internal methods
	_onRemove = () => {
		const {index, onRemove} = this.props;
		onRemove(index);
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/AttachFiles/Futur/AttachFiles.js